// resolveItem — render-time resolver for AI items + their repairs.
//
// Session 19AU: same role for repairs that `resolvedField` plays for
// itemOverrides — view code reads through this helper instead of touching
// the AI item directly. When a repair record exists with current !== null,
// the repaired version replaces the AI original; otherwise the AI original
// passes through unchanged.
//
// Why both this AND resolvedField:
//   - resolvedField is FIELD-level (e.g. mcq.explanation only)
//   - resolveItem is ITEM-level (entire mcq replaced atomically)
// They compose: a repaired MCQ may itself have a field override (rare, but
// allowed — user can fine-tune one field after a repair). Order matters:
// repair first (whole item), then field override on top.

import { repairKey, type RepairRecord } from '@examforge/shared/schemas';
import type { CourseProgress } from '@examforge/shared/schemas';

/** Item kinds participating in the repair system — must match RepairRecord.itemKind. */
export type ResolvableKind = RepairRecord['itemKind'];

/** Resolve a single AI item against the progress.repairs map.
 *  - If a repair exists with a current accepted version → return that.
 *  - Otherwise return the AI original unchanged.
 *  - Returns the same reference as input when no repair → caller can safely
 *    use referential equality for memo keys. */
export function resolveItem<T extends { id: string }>(
  kind: ResolvableKind,
  aiItem: T,
  repairs: CourseProgress['repairs'],
): T {
  const rec = repairs?.[repairKey(kind, aiItem.id)];
  if (!rec || rec.current === null || rec.current === undefined) return aiItem;
  // Cast: the repair pipeline schema-validates against the same kind's
  // schema before storing, so `current` shape matches T.
  return rec.current as T;
}

/** Bulk resolve — apply resolveItem across an array. */
export function resolveItems<T extends { id: string }>(
  kind: ResolvableKind,
  aiItems: readonly T[],
  repairs: CourseProgress['repairs'],
): T[] {
  if (!repairs || Object.keys(repairs).length === 0) return aiItems as T[];
  return aiItems.map((it) => resolveItem(kind, it, repairs));
}

/** Lookup repair record for a given item. Returns undefined when no record. */
export function getRepairRecord(
  kind: ResolvableKind,
  itemId: string,
  repairs: CourseProgress['repairs'],
): RepairRecord | undefined {
  return repairs?.[repairKey(kind, itemId)];
}
